import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UserChannelState {
  links: {
    next: string | null;
    previous: string | null;
  };
  previousDate: string | null;
  nextDate: string | null;
  results: any[];
  newActivityIds: string[];
  prevData?: any | null; // track prev data if loaded already
  hasLoadSpecificData: boolean;
}

const MAX_LENGTH = 36;
const FINAL_COUNT = 24;

export interface UserChannelStateByChannelId {
  [channelId: string]: UserChannelState;
}

const initialState: UserChannelStateByChannelId = {};

const userChannelActivitySlice = createSlice({
  name: 'userChannel',
  initialState,
  reducers: {
    setUserChannelData: (state, action: PayloadAction<{ channelId: string; data: UserChannelState }>) => {
      const { channelId, data } = action.payload;
      state[channelId] = {
        ...data,
        newActivityIds: state[channelId]?.newActivityIds?.length ? state[channelId].newActivityIds : [],
        hasLoadSpecificData: false
      };
    },
    insertPreviousActivities: (state, action: PayloadAction<{ channelId: string; data: UserChannelState }>) => {
      const { channelId, data } = action.payload;
      const currentState = state[channelId] || initialState;
      const combinedResults = [...data.results, ...currentState.results];

      // Only slice if combinedResults has at least 72 items
      const trimmedResults =
        combinedResults.length >= MAX_LENGTH ? combinedResults.slice(0, FINAL_COUNT) : combinedResults;
      state[channelId] = {
        ...currentState,
        links: { ...currentState.links, previous: data.links.previous },
        previousDate: data.previousDate,
        results: trimmedResults
      };
    },
    insertNextActivities: (state, action: PayloadAction<{ channelId: string; data: UserChannelState }>) => {
      const { channelId, data } = action.payload;
      const currentState = state[channelId] || initialState;
      const combinedResults = [...currentState.results, ...data.results];
      // Only slice if combinedResults has at least 72 items

      const trimmedResults =
        combinedResults.length >= MAX_LENGTH
          ? combinedResults.slice(combinedResults.length - FINAL_COUNT)
          : combinedResults;

      state[channelId] = {
        ...currentState,
        links: { ...currentState.links, next: data.links.next },
        nextDate: data.nextDate,
        results: trimmedResults
      };
    },

    // insertNextActivities: (state, action: PayloadAction<{ channelId: string; data: UserChannelState }>) => {
    //   const { channelId, data } = action.payload;
    //   const currentState = state[channelId] || initialState;

    //   console.log('insertNextActivities');
    //   state[channelId] = {
    //     ...currentState,
    //     links: { ...currentState.links, next: data.links.next },
    //     nextDate: data.nextDate,
    //     results: [...currentState.results, ...data.results]
    //   };
    // },
    // New reducer: reset only a specific channel's state
    resetChannelState: (state, action: PayloadAction<{ channelId: string }>) => {
      delete state[action.payload.channelId];
    },
    addNewActivity: (state, action: PayloadAction<{ channelId: string; activity: any }>) => {
      const { channelId, activity } = action.payload;
      if (!state[channelId]) {
        state[channelId] = {
          links: { next: null, previous: null },
          previousDate: null,
          nextDate: null,
          results: [],
          newActivityIds: [], // Initialize the array to hold new activity IDs,
          hasLoadSpecificData: false
        };
      }
      if (state[channelId].results) {
        // append new activity
        const results = state[channelId].results;
        const lastItem = results[results.length - 1];
        if (!lastItem?.nextUrl) {
          state[channelId].results = [...state[channelId].results, { ...activity }];
        }
        // state[channelId].results.unshift({ ...activity, isNew: true });
      }
      // Ensure newActivityIds is always an array
      if (!Array.isArray(state[channelId].newActivityIds)) {
        state[channelId].newActivityIds = []; // Reinitialize if it's somehow undefined or not an array
      }
      state[channelId].newActivityIds.push(activity.event_id); // Add the new activity ID
    },
    markActivitiesAsRead: (state, action: PayloadAction<{ channelId: string }>) => {
      const { channelId } = action.payload;

      if (state[channelId]) {
        const { newActivityIds } = state[channelId];
        if (newActivityIds.length > 0) {
          state[channelId].newActivityIds = []; // Clear the array after marking as read
        }
      }
    },
    markActivityAsNotNew: (state, action: PayloadAction<{ channelId: string; eventId: string }>) => {
      const { channelId, eventId } = action.payload;

      if (state[channelId]) {
        // state[channelId].results = state[channelId].results.map((activity) =>
        //   activity.event_id === eventId ? { ...activity, isNew: false } : activity
        // );
        // Also remove the event_id from newActivityIds
        state[channelId].newActivityIds = state[channelId].newActivityIds.filter((id) => id !== eventId);
      }
    },
    updateActivityVideoDetails: (
      state,
      action: PayloadAction<{ channelId: string; videoHashId: string; video: any }>
    ) => {
      // Find all results with the same hashId and update them
      const { channelId, videoHashId, video } = action.payload;
      if (state[channelId]) {
        state[channelId].results = state[channelId].results.map((item) => {
          if (item.content_object?.hash_id === videoHashId) {
            return {
              ...item,
              content_object: video // Update the content_object
            };
          }
          return item;
        });
      }
    },
    updateActivityVideoField: (
      state,
      action: PayloadAction<{ channelId: string; videoHashId: string; field: string; value: any }>
    ) => {
      const { channelId, videoHashId, field, value } = action.payload;
      if (state[channelId]) {
        state[channelId].results = state[channelId].results.map((item) => {
          if (item.content_object?.hash_id === videoHashId) {
            return {
              ...item,
              content_object: {
                ...item.content_object,
                [field]: value // Update only the specified field
              }
            };
          }
          return item;
        });
      }
    },
    deleteActivityVideo: (state, action: PayloadAction<{ channelId: string; videoHashId: string }>) => {
      const { channelId, videoHashId } = action.payload;
      if (state[channelId]) {
        state[channelId].results = state[channelId].results.filter(
          (activity) => activity.content_object?.hash_id !== videoHashId
        );
      }
    },
    updateActivityVideoChannels: (
      state,
      action: PayloadAction<{
        channelId: string;
        videoHashId: string;
        channel: any;
        type: 'add' | 'remove' | 'archived' | 'reshared';
      }>
    ) => {
      const { channelId, videoHashId, channel, type } = action.payload;
      if (state[channelId]) {
        state[channelId].results = state[channelId].results.map((item) => {
          if (item.content_object?.hash_id === videoHashId) {
            let updatedChannels = item.content_object.channels || [];
            if (type === 'add') {
              // Add the channel if it's not already in the array
              const exists = updatedChannels.some((ch: any) => ch.channel.id === channel.id);
              if (!exists) {
                updatedChannels = [...updatedChannels, { channel, is_archived: false }];
              }
            } else if (type === 'remove') {
              // Remove the channel if it exists in the array
              updatedChannels = updatedChannels.filter((ch: any) => ch.channel.id !== channel.id);
            } else if (type === 'archived') {
              // Update the is_archived field to true for the matching channel
              updatedChannels = updatedChannels.map((ch: any) =>
                ch.channel.id === channel.id ? { ...ch, is_archived: true } : ch
              );
            } else if (type === 'reshared') {
              // Update the is_archived field to false for the matching channel
              updatedChannels = updatedChannels.map((ch: any) =>
                ch.channel.id === channel.id ? { ...ch, is_archived: false } : ch
              );
            }

            return {
              ...item,
              content_object: {
                ...item.content_object,
                channels: updatedChannels
              }
            };
          }
          return item;
        });
      }
    },
    handleSpecificData: (state, action: PayloadAction<{ channelId: string; value: boolean }>) => {
      const { channelId, value } = action.payload;
      if (state[channelId]) {
        state[channelId].hasLoadSpecificData = value;
      }
    }
  }
});

export const {
  setUserChannelData,
  insertPreviousActivities,
  insertNextActivities,
  resetChannelState,
  addNewActivity,
  markActivitiesAsRead,
  markActivityAsNotNew,
  updateActivityVideoDetails,
  deleteActivityVideo,
  updateActivityVideoField,
  updateActivityVideoChannels,
  handleSpecificData
} = userChannelActivitySlice.actions;

export const UserChannelActivityReducers = userChannelActivitySlice.reducer;
